/**
 * COLMANTS — ATC TOAST + CART DRAWER CONTROL (all-in-one)
 *
 * Does three things:
 *   1. Stops the MoonBundle cart drawer from auto-opening after add-to-cart,
 *      WITHOUT breaking user-initiated opens (cart icon, toast link).
 *   2. Shows the in-card "added to cart" toast reliably.
 *   3. Keeps the header cart bubble in sync with cart.js.
 *
 * Load AFTER your theme's global.js (needs subscribe / PUB_SUB_EVENTS if
 * available, but degrades gracefully without them).
 *
 * If the drawer STILL auto-opens with this installed, the open is happening
 * via a class toggle rather than a function call — set
 * CONFIG.useClassFallback = true below.
 */

(function () {
  'use strict';

  /* ================================================================
     CONFIG
     ================================================================ */
  const CONFIG = {
    // How long after an ATC click to suppress programmatic drawer opens.
    suppressMs: 1500,

    // How long the toast stays visible.
    toastVisibleMs: 7000,

    // Debounce for cart.js polling when the drawer mutates.
    syncDebounceMs: 250,

    // Enable only if the drawer opens by class toggle instead of a
    // callable function (see header note). Costs a MutationObserver.
    useClassFallback: false,

    // Selectors — adjust to match your theme/app markup.
    drawer: '.moonbundle-cart-drawer',
    toast: '.atc-toast-notification',
    globalToast: '.atc-toast-notification[data-toast-global]',
    atcButtons: 'button[name="add"], .product-form__submit, .quick-add__submit',
    productCards:
      '.main-product-page, .main-collection-product-grid-item, ' +
      '.featured-product-main, .card-wrapper, .product-card',

    // Anything here = "the user wants the cart open", never suppress.
    openIntent: [
      '#cart-icon-bubble',
      '[href$="/cart"]',
      '[data-moonbundle-open]',
      '[data-toast-link]'
    ].join(', '),

    debug: false
  };

  const log = (...a) => CONFIG.debug && console.log('[ATC]', ...a);

  /* ================================================================
     1. AUTO-OPEN SUPPRESSION
     ================================================================ */
  let suppressUntil = 0;
  let userInitiated = false;

  const isSuppressed = () => !userInitiated && Date.now() < suppressUntil;

  /** Open the drawer on the user's behalf, bypassing suppression. */
  function openCartDrawerForUser() {
    userInitiated = true;
    suppressUntil = 0;
    try {
      if (typeof window.openMoonBundleCartDrawer === 'function') {
        window.openMoonBundleCartDrawer();
      } else {
        // No API available — fall back to the cart page.
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    } finally {
      userInitiated = false;
    }
  }
  window.openCartDrawerForUser = openCartDrawerForUser;

  function patchOpenFn() {
    const original = window.openMoonBundleCartDrawer;
    if (typeof original !== 'function' || original.__atcPatched) return false;

    const wrapped = function (...args) {
      if (isSuppressed()) {
        log('suppressed programmatic drawer open');
        return;
      }
      return original.apply(this, args);
    };
    wrapped.__atcPatched = true;

    try {
      window.openMoonBundleCartDrawer = wrapped;
      log('patched openMoonBundleCartDrawer');
      return true;
    } catch (_) {
      return false;
    }
  }

  // Patch now; retry briefly in case MoonBundle loads after us.
  if (!patchOpenFn()) {
    let tries = 0;
    const t = setInterval(() => {
      if (patchOpenFn() || ++tries > 60) clearInterval(t);
    }, 250);
  }

  // Optional fallback: revert class-toggle opens during the suppress window.
  let classObserver = null;
  function startClassFallback() {
    const drawer = document.querySelector(CONFIG.drawer);
    if (!drawer || classObserver) return;

    classObserver = new MutationObserver(() => {
      if (!isSuppressed()) return;
      if (drawer.classList.contains('open') || drawer.hasAttribute('data-open')) {
        drawer.classList.remove('open');
        drawer.removeAttribute('data-open');
        log('reverted class-toggle drawer open');
      }
    });
    classObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['class', 'data-open']
    });
  }

  /* ================================================================
     2. TOAST
     ================================================================ */
  let lastClickedProductCard = null;
  const toastTimers = new WeakMap();

  function showToast() {
    const toast =
      (lastClickedProductCard &&
        lastClickedProductCard.querySelector(CONFIG.toast)) ||
      document.querySelector(CONFIG.globalToast);

    if (!toast) {
      log('no toast element found for this card');
      return;
    }

    toast.classList.add('is-visible');
    clearTimeout(toastTimers.get(toast));
    toastTimers.set(
      toast,
      setTimeout(() => toast.classList.remove('is-visible'), CONFIG.toastVisibleMs)
    );
  }

  function hideAllToasts() {
    document
      .querySelectorAll(`${CONFIG.toast}.is-visible`)
      .forEach((t) => t.classList.remove('is-visible'));
  }

  /* ================================================================
     CLICK HANDLING (capture phase — wins the race against the app)
     ================================================================ */
  document.addEventListener(
    'click',
    (e) => {
      // User explicitly opening the cart: clear suppression, let it through.
      if (e.target.closest(CONFIG.openIntent)) {
        suppressUntil = 0;
        return;
      }

      const atc = e.target.closest(CONFIG.atcButtons);
      if (!atc) return;
      if (atc.disabled || atc.classList.contains('sold-out')) return;

      lastClickedProductCard = atc.closest(CONFIG.productCards);
      suppressUntil = Date.now() + CONFIG.suppressMs;
      log('ATC click — suppressing auto-open');
    },
    true
  );

  // Toast buttons (delegated, so dynamically injected cards work).
  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-toast-close]');
    if (closeBtn) {
      e.stopPropagation();
      closeBtn.closest(CONFIG.toast)?.classList.remove('is-visible');
      return;
    }

    const link = e.target.closest('[data-toast-link]');
    if (link) {
      e.preventDefault();
      e.stopPropagation();
      hideAllToasts();
      openCartDrawerForUser();
    }
  });

  // Escape hatch — never leave the drawer wedged.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') suppressUntil = 0;
  });

  /* ================================================================
     CART UPDATE EVENTS
     ================================================================ */
  function handleCartUpdate(cartData) {
    showToast();
    syncBubble();

    document.dispatchEvent(
      new CustomEvent('moonbundle:cart-updated', {
        detail: { cartData, timestamp: Date.now() }
      })
    );

    if (typeof window.refreshMoonBundleCart === 'function') {
      try {
        window.refreshMoonBundleCart(cartData);
      } catch (_) {}
    }
  }

  // NOTE: deliberately no `event.source === 'product-form'` filter —
  // quick-add publishes a different source and was silently dropping toasts.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => handleCartUpdate(event?.cartData));
  }

  document.addEventListener('cart:section-updated', (e) =>
    handleCartUpdate(e.detail?.cartData)
  );
  document.addEventListener('cart:updated', (e) => handleCartUpdate(e.detail?.cart));

  /* ================================================================
     3. CART BUBBLE SYNC (debounced + single-flight)
     ================================================================ */
  let inFlight = false;
  let queued = false;
  let syncTimer = null;

  function applyCount(itemCount) {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;

    const countSpan = bubble.querySelector('span[aria-hidden="true"]');
    const srSpan = bubble.querySelector('.visually-hidden');

    if (countSpan) countSpan.textContent = itemCount;
    if (srSpan) srSpan.textContent = `${itemCount} item${itemCount !== 1 ? 's' : ''}`;
    bubble.style.display = itemCount === 0 ? 'none' : 'flex';
  }

  function fetchAndSync() {
    if (inFlight) {
      queued = true;
      return;
    }
    inFlight = true;

    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' }
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((cart) => applyCount(cart.item_count || 0))
      .catch((err) => log('cart.js sync failed', err))
      .finally(() => {
        inFlight = false;
        if (queued) {
          queued = false;
          syncBubble();
        }
      });
  }

  function syncBubble() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(fetchAndSync, CONFIG.syncDebounceMs);
  }

  // NOTE: the old "force bubble to 0 if .moonbundle-cart-empty exists" hack
  // is gone — that element often exists as a hidden template and was
  // zeroing out a non-empty cart. cart.js item_count is the only truth.

  let bubbleObserver = null;
  function startObserving() {
    const drawer = document.querySelector(CONFIG.drawer);
    if (drawer && !bubbleObserver) {
      bubbleObserver = new MutationObserver(syncBubble);
      bubbleObserver.observe(drawer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-open', 'class']
      });
    }
    if (CONFIG.useClassFallback) startClassFallback();
    fetchAndSync();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncBubble);
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) syncBubble();
  });

  document.addEventListener('shopify:section:load', () => {
    // Drawer may have been re-rendered; re-attach observers.
    if (bubbleObserver) {
      bubbleObserver.disconnect();
      bubbleObserver = null;
    }
    if (classObserver) {
      classObserver.disconnect();
      classObserver = null;
    }
    startObserving();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }

  log('initialized');
})();